import React from "react";
import {
  Toast,
  ToastTitle,
  ToastDescription,
  ToastClose,
  ToastAction,
} from "./Toast";
import { ToastIconShell } from "./ToastIconShell";
import { AlertCircle, CircleCheck, ExternalLink } from "lucide-react";

export interface OverdueTaskToastProps {
  count: number;
  taskTitle?: string;
  roLabel?: string;
  onComplete?: () => void;
  onViewTasks?: () => void;
  onDismiss?: () => void;
}

export const OverdueTaskToast = React.forwardRef<
  React.ElementRef<typeof Toast>,
  OverdueTaskToastProps
>(
  (
    { count, taskTitle, roLabel, onComplete, onViewTasks, onDismiss, ...props },
    ref
  ) => {
    const isDetail = count === 1;

    return (
      <Toast
        ref={ref}
        variant="default"
        className="border-t-4 border-t-destructive"
        {...props}
        duration={Infinity}
      >
        <ToastIconShell
          icon={<AlertCircle className={"h-4 w-4 text-destructive"} />}
        >
          {isDetail ? (
            <>
              <ToastTitle>Task overdue</ToastTitle>
              <ToastDescription>
                {taskTitle ?? "A task"}
                {roLabel ? ` — RO ${roLabel}` : ""}
              </ToastDescription>
            </>
          ) : (
            <>
              <ToastTitle>{count} tasks overdue</ToastTitle>
              <ToastDescription>
                You have {count} overdue tasks.
              </ToastDescription>
            </>
          )}
        </ToastIconShell>
        {isDetail ? (
          <>
            {onComplete && (
              <ToastAction
                altText="Mark complete"
                aria-label="Mark complete"
                title="Mark complete"
                onClick={onComplete}
                className="h-8 w-8 shrink-0 border-success/40 p-0 text-success hover:border-success/30 hover:bg-success hover:text-success-foreground focus:ring-success"
              >
                <CircleCheck className="h-4 w-4" />
              </ToastAction>
            )}
            {onViewTasks && (
              <ToastAction
                altText="Open task"
                aria-label="Open task"
                title="Open task"
                onClick={onViewTasks}
                className="h-8 w-8 shrink-0 border-primary/40 p-0 text-primary hover:border-primary/30 hover:bg-primary hover:text-primary-foreground focus:ring-primary"
              >
                <ExternalLink className="h-4 w-4" />
              </ToastAction>
            )}
          </>
        ) : (
          onViewTasks && (
            <ToastAction
              altText="View tasks"
              onClick={onViewTasks}
              className="hover:text-secondary-foreground"
            >
              View tasks
            </ToastAction>
          )
        )}
        <ToastClose onClick={onDismiss} />
      </Toast>
    );
  }
);

OverdueTaskToast.displayName = "OverdueTaskToast";
